import React from 'react';
import PropTypes from 'prop-types';

import { Wrapper, IconWrapper, LabelWrapper } from './index.styles';

import Icon from '../icon';

import { isClickable } from '../utils';

/* eslint-disable arrow-body-style */

/**
 * Renders a badge. Supports icons.
 */
const Badge = React.forwardRef((props, ref) => {
  const {
    as,
    children,
    color = 'primary',
    faded = false,
    size = 'regular',
    leftIconHeight = size === 'small' ? 14 : 16,
    leftIconSrc,
    leftIconWidth = size === 'small' ? 14 : 16,
    onClick,
    onKeyDown,
    onMouseDown,
    rightIconHeight = size === 'small' ? 14 : 16,
    rightIconSrc,
    rightIconWidth = size === 'small' ? 14 : 16,
    ...otherProps
  } = props;

  return (
    <Wrapper
      as={as || (isClickable(props) ? 'button' : undefined)}
      color={color}
      onClick={onClick}
      onKeyDown={onKeyDown}
      onMouseDown={onMouseDown}
      faded={faded}
      ref={ref}
      size={size}
      {...otherProps}
    >
      {leftIconSrc && (
        <IconWrapper color={color}>
          <Icon alt="" src={leftIconSrc} width={leftIconWidth} height={leftIconHeight} inline />
        </IconWrapper>
      )}
      {children && <LabelWrapper color={color}>{children}</LabelWrapper>}
      {rightIconSrc && (
        <IconWrapper color={color}>
          <Icon alt="" src={rightIconSrc} width={rightIconWidth} height={rightIconHeight} inline />
        </IconWrapper>
      )}
    </Wrapper>
  );
});

Badge.displayName = 'Badge';

Badge.propTypes = {
  /**
   * Component that should render the badge.
   */
  as: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.string]),
  /**
   * Badge label.
   */
  children: PropTypes.node,
  /**
   * Badge color.
   */
  color: PropTypes.oneOf([
    'black',
    'white',
    'coolGray',
    'red',
    'yellow',
    // Deprecated, but kept for backwards compatibility
    'orange',
    'teal',
    'blue',
    'indigo',
    // Deprecated, but kept for backwards compatibility
    'purple',
    'pink',
    'primary',
  ]),
  /**
   * Whether badge background should be faded.
   */
  faded: PropTypes.bool,
  /**
   * Left icon height in pixels (recommended if icon URL is used).
   */
  leftIconHeight: PropTypes.number,
  /**
   * Left icon, left icon source or left icon URL.
   */
  leftIconSrc: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.node,
    PropTypes.object,
    PropTypes.string,
  ]),
  /**
   * Left icon width in pixels (recommended if icon URL is used).
   */
  leftIconWidth: PropTypes.number,
  /**
   * Function to run on click.
   */
  onClick: PropTypes.func,
  /**
   * Function to run on key down.
   */
  onKeyDown: PropTypes.func,
  /**
   * Function to run on mouse down.
   */
  onMouseDown: PropTypes.func,
  /**
   * Right icon height in pixels (recommended if icon URL is used).
   */
  rightIconHeight: PropTypes.number,
  /**
   * Right icon, right icon source or right icon URL.
   */
  rightIconSrc: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.node,
    PropTypes.object,
    PropTypes.string,
  ]),
  /**
   * Right icon width in pixels (recommended if icon URL is used).
   */
  rightIconWidth: PropTypes.number,
  /**
   * Badge size.
   */
  size: PropTypes.oneOf(['regular', 'small']),
};

export default Badge;
